import React from 'react';
import { motion } from 'framer-motion';
import { Sun, Moon, Monitor, Palette, Zap, Heart, Activity, Brain } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import { useTheme } from '../styles/theme-manager';

const ThemeDemo: React.FC = () => {
  const { theme, effectiveTheme, systemPreference } = useTheme();

  const features = [
    {
      icon: Palette,
      title: "Warm Yellow Palette",
      description: "Beautiful warm yellow/white colors for light mode as requested",
      color: "text-yellow-500"
    },
    {
      icon: Zap,
      title: "Smooth Transitions",
      description: "300ms animations between themes for seamless experience",
      color: "text-blue-500"
    },
    {
      icon: Heart,
      title: "WCAG AA Compliant",
      description: "All color combinations meet accessibility standards",
      color: "text-green-500"
    },
    {
      icon: Monitor,
      title: "System Integration",
      description: "Automatic detection of OS/Browser color preferences",
      color: "text-purple-500"
    },
    {
      icon: Activity,
      title: "Cross-Browser Support",
      description: "Works on Chrome, Firefox, Safari, Edge with fallbacks",
      color: "text-orange-500"
    },
    {
      icon: Brain,
      title: "Persistent Preferences",
      description: "User theme choice saved across sessions",
      color: "text-pink-500"
    }
  ];

  const colorPalette = [
    { name: "Background", class: "bg-theme-background", hex: "var(--theme-background)" },
    { name: "Surface", class: "bg-theme-surface", hex: "var(--theme-surface)" },
    { name: "Primary Text", class: "text-theme-primary bg-theme-background p-2", hex: "var(--theme-text-primary)" },
    { name: "Secondary Text", class: "text-theme-secondary bg-theme-background p-2", hex: "var(--theme-text-secondary)" },
    { name: "Muted Text", class: "text-theme-muted bg-theme-background p-2", hex: "var(--theme-text-muted)" },
    { name: "Primary Accent", class: "bg-theme-primary text-white p-2", hex: "var(--theme-primary)" },
    { name: "Secondary Accent", class: "bg-theme-secondary text-white p-2", hex: "var(--theme-secondary)" },
    { name: "Success", class: "bg-theme-success text-white p-2", hex: "var(--theme-success)" },
    { name: "Warning", class: "bg-theme-warning text-white p-2", hex: "var(--theme-warning)" },
    { name: "Error", class: "bg-theme-error text-white p-2", hex: "var(--theme-error)" }
  ];

  return (
    <div className="min-h-screen bg-theme-background text-theme-primary transition-all duration-300 ease-in-out">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-theme-surface/90 backdrop-blur-sm border-b border-theme-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              {effectiveTheme === 'dark' ? (
                <Moon className="w-6 h-6 text-yellow-400" />
              ) : (
                <Sun className="w-6 h-6 text-orange-500" />
              )}
              <div>
                <h1 className="text-xl font-bold">BioTune Theme System</h1>
                <p className="text-xs text-theme-secondary">
                  {theme === 'system' ? `System (${systemPreference})` : `Manual (${theme})`}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <ThemeToggle variant="dropdown" size="md" />
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-theme-surface to-theme-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Comprehensive Day/Night Mode
            </h2>
            <p className="text-xl text-theme-secondary max-w-3xl mx-auto mb-8">
              Experience seamless theme switching with WCAG AA compliant colors, 
              smooth animations, and intelligent system integration.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <ThemeToggle variant="button" size="lg" />
              <ThemeToggle variant="button" size="md" showLabel={true} />
              <ThemeToggle variant="button" size="sm" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-3xl font-bold mb-4">Key Features</h3>
            <p className="text-lg text-theme-secondary max-w-2xl mx-auto">
              Built with modern web standards and accessibility in mind
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-theme-surface rounded-xl p-6 border border-theme-border hover:border-theme-primary transition-all duration-300"
                >
                  <div className={`${feature.color} mb-4`}>
                    <Icon className="w-8 h-8" />
                  </div>
                  <h4 className="text-lg font-semibold mb-2">{feature.title}</h4>
                  <p className="text-theme-secondary">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Color Palette */}
      <section className="py-16 bg-theme-surface/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-3xl font-bold mb-4">Theme Color Palette</h3>
            <p className="text-lg text-theme-secondary max-w-2xl mx-auto">
              Carefully selected colors that meet WCAG AA standards
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {colorPalette.map((color, index) => (
              <motion.div
                key={color.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-theme-background rounded-lg p-4 border border-theme-border"
              >
                <div className={`h-16 rounded-lg mb-3 ${color.class}`}></div>
                <h4 className="font-semibold mb-1">{color.name}</h4>
                <p className="text-sm text-theme-secondary font-mono">{color.hex}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Demo */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-3xl font-bold mb-4">Interactive Demo</h3>
            <p className="text-lg text-theme-secondary max-w-2xl mx-auto">
              Try different theme toggle variations and see the smooth transitions
            </p>
          </motion.div>

          <div className="bg-theme-surface rounded-xl p-8 border border-theme-border">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Left Column - Controls */}
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-semibold mb-4">Theme Toggle Variants</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-theme-background rounded-lg">
                      <span className="text-theme-secondary">Button Style</span>
                      <ThemeToggle variant="button" size="md" />
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-theme-background rounded-lg">
                      <span className="text-theme-secondary">Switch Style</span>
                      <ThemeToggle variant="button" size="md" showLabel={false} />
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-theme-background rounded-lg">
                      <span className="text-theme-secondary">Dropdown Style</span>
                      <ThemeToggle variant="dropdown" size="md" />
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-theme-background rounded-lg">
                      <span className="text-theme-secondary">Icon Style</span>
                      <ThemeToggle variant="button" size="md" />
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-semibold mb-4">Current Status</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-theme-background rounded-lg">
                      <span className="text-theme-secondary">Selected Theme</span>
                      <span className="font-semibold capitalize">{theme}</span>
                    </div>
                    
                    <div className="flex justify-between items-center p-3 bg-theme-background rounded-lg">
                      <span className="text-theme-secondary">Effective Theme</span>
                      <span className="font-semibold capitalize">{effectiveTheme}</span>
                    </div>
                    
                    <div className="flex justify-between items-center p-3 bg-theme-background rounded-lg">
                      <span className="text-theme-secondary">System Preference</span>
                      <span className="font-semibold capitalize">{systemPreference}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Preview */}
              <div className="space-y-6">
                <h4 className="text-lg font-semibold mb-4">Theme Preview</h4>
                
                <div className="bg-theme-background rounded-lg p-6 border border-theme-border">
                  <h5 className="text-theme-primary font-semibold mb-2">Primary Text</h5>
                  <p className="text-theme-secondary mb-4">This is secondary text with excellent contrast ratio.</p>
                  <p className="text-theme-muted text-sm">And this is muted text for less important information.</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <button className="bg-theme-primary text-white py-3 px-4 rounded-lg font-medium hover:opacity-90 transition-opacity">
                    Primary Button
                  </button>
                  
                  <button className="border-2 border-theme-border text-theme-primary py-3 px-4 rounded-lg font-medium hover:bg-theme-surface transition-colors">
                    Secondary Button
                  </button>
                </div>
                
                <div className="glass-surface rounded-lg p-6">
                  <h5 className="text-theme-primary font-semibold mb-2">Glass Surface</h5>
                  <p className="text-theme-secondary text-sm">
                    This demonstrates the glass morphism effect with proper theme integration.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-theme-surface border-t border-theme-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-theme-secondary mb-4">
            Experience the full theme system with WCAG AA compliance and smooth transitions.
          </p>
          <div className="flex justify-center gap-4">
            <ThemeToggle variant="button" size="lg" />
            <ThemeToggle variant="button" size="md" showLabel={true} />
            <ThemeToggle variant="dropdown" size="md" />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ThemeDemo;